/// <amd-module name="Repos/Views/FilesHub/Components/FilesHeader" />
import { GitVersionType } from "Repos/Common/Contracts/Git";
import { getDefaultCreateBranchAction } from "Repos/Common/ReposActionService";
import { getVersionString } from "Repos/Common/Util/Version";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IGitItem } from "Repos/Files/Contracts";
import { FilesEditableBreadcrumb } from "Repos/FilesHubCommon/Components/FilesEditableBreadcrumb";
import { FilesHeaderCommandBar } from "Repos/FilesHubCommon/Components/FilesHeaderCommandBar";
import { ICreateBranchDialogProps } from "Repos/VersionDialogs/Dialogs.Props";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import * as Resources from "Repos/Views/FilesHub/Resources";
import { IOrganizationAadData } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { EmptyGuidString } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IStartRightExtensionService } from "VSS/Platform/StartRightExtension";
import { Button } from "VSSUI/Button";
import { ClipboardButton } from "VSSUI/Clipboard";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import * as ClipboardResources from "VSSUI/Resources.Clipboard";
import { css } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import * as React from "react";
import { IFilesHubViewService, getFilesHubTabsService } from "../FilesHubViewService";


export interface IContributedLanguageProvider {
    availableLanguages: string[];
    getToolTipProvider?(filePath: string, ref?: string, isCommit?: boolean): Promise<IIndexingToolTipProvider | undefined>;
}

export interface IIndexingToolTipProvider {
    displayToolTip: boolean;
    buttonText: string;
    buttonIcon: string;
    buttonColor: string;
    onClickRef: string;
}

export const NotInitializedButton: IIndexingToolTipProvider = {
    displayToolTip: false,
    buttonColor: '',
    buttonIcon: 'Error',
    buttonText: Resources.IndexingButtonInitialText,
    onClickRef: Resources.IndexingInitialOnClickRef
}

export const FilesHeader = (props: { pageContext: IVssPageContext }): JSX.Element => {
    const { pageContext } = props;
    const [indexingContribution, setIndexingContribution] = React.useState<IContributedLanguageProvider>();
    const [indexingButtonProperties, setIndexingButtonProperties] = React.useState<IIndexingToolTipProvider>();
    const [currentFile, setCurrentFile] = React.useState<string>();

    // Checks if the repo and current file is indexed
    const checkIfIndexingInformationAvailable = React.useCallback(async (item: IGitItem) => {
        if (item.name !== currentFile) {
            const commitId: string | undefined = item.commitId;
            // since the item path starts with a / substring(1) returns everything past the first /
            const indexingToolTipResponse = await indexingContribution?.getToolTipProvider!(item.path.substring(1), commitId, true);
            if (indexingToolTipResponse) {
                setIndexingButtonProperties({
                    displayToolTip: indexingToolTipResponse.displayToolTip,
                    buttonText: indexingToolTipResponse.buttonText,
                    buttonColor: indexingToolTipResponse.buttonColor,
                    buttonIcon: indexingToolTipResponse.buttonIcon,
                    onClickRef: indexingToolTipResponse.onClickRef
                });
                setCurrentFile(item.name);
            }
        }
    }, [indexingContribution, indexingButtonProperties, currentFile]);

    React.useEffect(() => {
        if (!indexingContribution) {
            // Gets indexing contribution type
            const getContributionsByType = async () => {
                const indexingContributionType = "ms.vss-features.code-editor-navigation-provider";
                const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
                const providerContributions = contributionService.getContributionsByType(indexingContributionType);
                for (const providerContributionId in providerContributions) {
                    const indexingContributionResponse: IContributedLanguageProvider | undefined = await contributionService.getService(providerContributionId);
                    setIndexingContribution({ availableLanguages: indexingContributionResponse?.availableLanguages!, getToolTipProvider: indexingContributionResponse?.getToolTipProvider });
                }
            };

            getContributionsByType();
        }
    }, []);

    return (
        <ScreenSizeObserver>
            {(screenSizeProps: { screenSize: ScreenSize }) => {
                const filesService = pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
                return (
                    <Observer item={filesService.getSelectedItem()}>
                        {(props: { item: IGitItem }) => {
                            const { item } = props;
                            const languageByExt: { [key: string]: string } = {
                                "cpp": "cpp",
                                "cs": "csharp",
                                "vb": "csharp",
                                "h": "cpp",
                                "java": "java",
                                "js": "javascript",
                                "jsx": "javascript",
                                "ts": "typescript",
                                "tsx": "typescript",
                            };

                            // Gets the file extension
                            const getFileExtensions = (item: IGitItem): string => {
                                return item.name.split('.').slice(-1)[0];
                            }

                            // Determines if the indexing button should be displayed
                            const checkIfIndexingExtensionIsAvailable = (item: IGitItem): boolean => {
                                if (indexingContribution && Object.keys(indexingContribution).length > 0) {
                                    const fileExtension = getFileExtensions(item);
                                    if (fileExtension) {
                                        return indexingContribution.availableLanguages.indexOf(languageByExt[fileExtension]) != -1;
                                    }
                                }
                                return false;
                            }

                            if (checkIfIndexingExtensionIsAvailable(item) && indexingContribution) {
                                checkIfIndexingInformationAvailable(item);
                            }

                            return (
                                <>
                                    <FilesHeaderPathRow item={item} pageContext={pageContext} screenSize={screenSizeProps.screenSize} />
                                    <CustomHeader className="repos-files-header bolt-header-with-commandbar">
                                        <HeaderTitleArea>
                                            <HeaderTitleRow>
                                                <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                                    {item.path === "/" ? Resources.Files : item.name}
                                                </HeaderTitle>
                                            </HeaderTitleRow>
                                            {
                                                checkIfIndexingExtensionIsAvailable(item) &&
                                                indexingButtonProperties?.displayToolTip &&
                                                <Button
                                                    style={{ background: 'None', paddingLeft: 'revert', color: indexingButtonProperties ? indexingButtonProperties?.buttonColor : NotInitializedButton.buttonColor }}
                                                    className="repos-files-header Indexing button"
                                                    ariaLabel={indexingButtonProperties ? indexingButtonProperties?.buttonText : NotInitializedButton.buttonText}
                                                    iconProps={{ iconName: indexingButtonProperties ? indexingButtonProperties?.buttonIcon : NotInitializedButton.buttonIcon }}
                                                    onClick={() => window.open(indexingButtonProperties ? indexingButtonProperties?.onClickRef : NotInitializedButton.onClickRef, '_blank')}
                                                />
                                            }
                                        </HeaderTitleArea>
                                        {item.path === "/" && <PipelineStatus pageContext={pageContext} />}
                                        <FilesHeaderCommandBar
                                            filesService={filesService}
                                            item={item}
                                            saveTitle={Resources.Commit}
                                            screenSize={screenSizeProps.screenSize}
                                        />
                                    </CustomHeader>
                                </>
                            );
                        }}
                    </Observer>
                );
            }}
        </ScreenSizeObserver>
    );
};

const FilesHeaderPathRow = (props: { item: IGitItem; pageContext: IVssPageContext; screenSize: ScreenSize }): JSX.Element => {
    const { item, pageContext, screenSize } = props;
    const filesService = pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
    const version = filesService.getVersion();
    const pathEditMode = new ObservableValue(false);
    const dropdown = React.createRef<VersionDropdown>();

    const shortcutService = pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
    React.useEffect(() => {
        shortcutService.registerShortcut(Resources.FilesShortcutGroup, {
            combos: ["w"],
            description: Resources.SelectBranchShortcutDescription,
            action: () => {
                if (dropdown.current) {
                    dropdown.current.focus();
                    dropdown.current.expand();
                }
            }
        });
        shortcutService.registerShortcut(Resources.FilesShortcutGroup, {
            combos: ["."],
            description: Resources.OpenInVsCodeForTheWeb,
            action: () => {
                const repositoryUrl = new URL(getGitRepository(pageContext)!.remoteUrl);
                repositoryUrl.search = filesService.getSelectedItem().value.href;
                //Workaround for cases when repository service return repo url with default collection for hosted server
                const vssPageService = pageContext.getService<IVssPageService>("IVssPageService");
                const vssPageData = vssPageService.getData();
                const repoPathname = vssPageData?.isHosted ? repositoryUrl.pathname.replace(Resources.DefaultCollection, "") : repositoryUrl.pathname;
                
                let repoVsCodeUrl = Resources.VsCodeUrl + repositoryUrl.hostname + repoPathname + repositoryUrl.search;

                if (isFeatureFlagEnabled(pageContext, "SourceControl.PassTenantIdToVSCodeShortcut", false)) {
                    //Pass tenant Id as a query string parameter to VS code for web
                    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
                    var orgSettings = contributionService.getData<IOrganizationAadData>("ms.vss-code-web.organization-admin-aad-data-provider");

                    if (orgSettings?.orgnizationTenantData?.id && orgSettings.orgnizationTenantData.id !== EmptyGuidString) {
                        repoVsCodeUrl = repoVsCodeUrl + "&tenantId=" + orgSettings.orgnizationTenantData.id;
                    }
                }

                onClickFPS(pageContext, repoVsCodeUrl, true);
            }
        });
        return () => {
            shortcutService.unRegisterShortcut(Resources.FilesShortcutGroup, "w");
            shortcutService.unRegisterShortcut(Resources.FilesShortcutGroup, ".");
        };
    });

    const onNewVersionSelected = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IVersionDropdownItem>): void => {
        const filesService = pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
        const tabsService = getFilesHubTabsService(pageContext);
        const currentVersionString = getVersionString(filesService.getVersion());

        const newVersion = item.data!.versionDescriptor;
        const newVersionString = getVersionString(newVersion);

        if (currentVersionString !== newVersionString) {
            const newVersionUrl = filesService.getItemUrl(filesService.getSelectedItem().value.path, {
                version: newVersionString,
                tabId: tabsService.getSelectedTabId().value
            });
            onClickFPS(pageContext, newVersionUrl, true, event);

            // Version will only change on navigation, so if navigation is cancelled, we should keep current version.
            dropdown.current?.resetSelection();
        }
    };

    return (
        <div className="repos-files-header-path-row body-m flex-row flex-center flex-noshrink scroll-hidden">
            <Observer editMode={pathEditMode} key="version">
                {(props: { editMode: boolean }) => {
                    return (
                        <VersionDropdown
                            className={css(props.editMode && screenSize <= ScreenSize.xsmall && "hidden")}
                            initiallySelectedVersion={version}
                            onSelect={onNewVersionSelected}
                            providerOptions={{
                                onCreateBranchClick: () => {
                                    // Give focus back to expandable since callout will soon unmount
                                    dropdown.current && dropdown.current.focus();
                                    getDefaultCreateBranchAction(
                                        pageContext,
                                        {
                                            referenceVersion: version
                                        } as ICreateBranchDialogProps,
                                        filesService.getSelectedItem().value.path
                                    )();
                                }
                            }}
                            ref={dropdown}
                        />
                    );
                }}
            </Observer>
            <ClipboardButton
                className="repos-files-branch-copy-button"
                getContent={() =>
                    (version.version)
                }
                key="clipboard"
                showCopiedTooltip={true}
                tooltipProps={{ text: ClipboardResources.CopyToClipboard }}
            />
            <FilesEditableBreadcrumb
                key="breadcrumb"
                canSearch={true}
                editMode={pathEditMode}
                filesService={filesService}
                onPathSelected={path => filesService.setSelectedPath(path)}
                selectedPath={item.path}
                screenSize={screenSize}
            />
        </div>
    );
};

const PipelineStatus = (props: { pageContext: IVssPageContext }) => {
    const pageContext = props.pageContext;

    const filesService = pageContext.getService<IFilesHubViewService>("IFilesHubViewService");
    const repository = getGitRepository(pageContext)!;
    const project = getTFSPageData(pageContext)!.project!;
    const version = filesService.getVersion();

    if (version.versionType !== GitVersionType.Branch) {
        return null;
    }

    const refName = "refs/heads/" + version.version;

    return (
        <WrappedComponent
            wrappedType="Repos.BranchPipelineStatus"
            dependencies={["ms.vss-code-web.pipeline-status-dependencies"]}
            className="flex-noshrink margin-horizontal-8 flex-self-center"
            projectId={project.id}
            repositoryId={repository.id}
            refName={refName}
            renderNoStatus={() => {
                const startRightExtensionService = pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
                const url = startRightExtensionService.getCreatePipelineUrlByRepository(pageContext, repository.id, repository.name, refName) ||
                            routeUrl(pageContext, "ms.vss-ciworkflow.ci-editor-route", {
                                project: project.name,
                                _a: "build-definition-getting-started",
                                source: "code",
                                repository: repository.name,
                                repositoryId: repository.id,
                                branchName: refName,
                                repositoryType: "TfsGit"
                            });

                return (
                    <Button
                        className="repos-files-header-setup-build-button"
                        iconProps={{ iconName: "Build" }}
                        primary={true}
                        text={Resources.SetupBuild}
                        href={url}
                    />
                );
            }}
        />
    );
};
